export function onlyDigits(value: string): string {
  return value.replace(/\D+/g, '')
}

export function isValidCPF(value: string): boolean {
  const cpf = onlyDigits(value)
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
    return false
  }

  const calcDigit = (base: string, factor: number) => {
    const total = base
      .split('')
      .reduce((sum, digit, index) => sum + Number(digit) * (factor - index), 0)
    const mod = (total * 10) % 11
    return mod === 10 ? 0 : mod
  }

  const first = calcDigit(cpf.slice(0, 9), 10)
  const second = calcDigit(cpf.slice(0, 10), 11)
  return cpf === `${cpf.slice(0, 9)}${first}${second}`
}

export function isValidCNPJ(value: string): boolean {
  const cnpj = onlyDigits(value)
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
    return false
  }

  const calcDigit = (base: string, weights: number[]) => {
    const total = base
      .split('')
      .reduce((sum, digit, index) => sum + Number(digit) * (weights[index] ?? 0), 0)
    const mod = total % 11
    return mod < 2 ? 0 : 11 - mod
  }

  const first = calcDigit(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  const second = calcDigit(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  return cnpj === `${cnpj.slice(0, 12)}${first}${second}`
}

export function isValidPhone(value: string): boolean {
  const digits = onlyDigits(value)
  return digits.length >= 10 && digits.length <= 15
}

export type PhoneCountry = 'BR' | 'US'

function isValidBrazilianPhoneDigits(value: string): boolean {
  if (value.length !== 10 && value.length !== 11) return false
  if (value.startsWith('0')) return false
  return !/^(\d)\1+$/.test(value)
}

function isValidUnitedStatesPhoneDigits(value: string): boolean {
  if (value.length === 10) return !/^(\d)\1+$/.test(value)
  if (value.length === 11 && value.startsWith('1')) return !/^(\d)\1+$/.test(value.slice(1))
  return false
}

export function normalizePhoneForCountry(value: string, country: PhoneCountry): string | null {
  const digits = onlyDigits(value)

  if (country === 'BR') {
    const nationalDigits =
      digits.startsWith('55') && (digits.length === 12 || digits.length === 13)
        ? digits.slice(2)
        : digits

    return isValidBrazilianPhoneDigits(nationalDigits) ? `+55${nationalDigits}` : null
  }

  if (digits.length === 10 && isValidUnitedStatesPhoneDigits(digits)) {
    return `+1${digits}`
  }

  return isValidUnitedStatesPhoneDigits(digits) ? `+${digits}` : null
}

export function isValidPhoneForCountry(value: string, country: PhoneCountry): boolean {
  return normalizePhoneForCountry(value, country) !== null
}
